<template>
  <div class="pages-status">
    <div class="status-card">
      <div class="status-header">
        <Icon name="material-symbols:description" class="header-icon" />
        <h3 class="status-title">{{ $t('pay.pagesStatus.title') }}</h3>
      </div>

      <div class="status-content">
        <!-- 未登录状态 -->
        <div v-if="!isLoggedIn" class="guest-hint">
          <span>{{ $t('pay.guest.prompt') }}</span>
          <button class="login-link" @click="handleLogin">{{ $t('pay.guest.login') }}</button>
        </div>

        <!-- 加载状态 -->
        <div v-else-if="isLoading" class="loading-state">
          <div class="skeleton-line w-20 h-32" />
        </div>

        <!-- 已登录且加载完成 -->
        <template v-else>
          <div class="pages-count">
            <span class="count-number">{{ membershipInfo.remainingPages }}</span>
            <span class="count-unit">{{ $t('pay.pagesStatus.unit') }}</span>
          </div>

          <div v-if="membershipInfo.temporaryPages > 0" class="expiry-hint">
            <Icon name="material-symbols:schedule" class="hint-icon" />
            <span>{{ expiryText }}</span>
          </div>
        </template>
      </div>

    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMyPageStore } from '~/stores/myPage'
import { useUserStore } from '~/stores/user'

const { t } = useI18n()
const myPageStore = useMyPageStore()
const userStore = useUserStore()

// 登录状态
const isLoggedIn = computed(() => userStore.isLoggedIn)
const isLoading = computed(() => myPageStore.membershipLoading)

// 会员信息
const membershipInfo = computed(() => myPageStore.membershipInfo)

// 过期提示文本
const expiryText = computed(() => {
  if (membershipInfo.value.temporaryPages > 0) {
    return t('pay.pagesStatus.expiry', {
      n: membershipInfo.value.temporaryPages,
      date: membershipInfo.value.nearestExpireDate
    })
  }
  return ''
})

const emit = defineEmits<{
  buyAgain: []
}>()

// 登录处理
const handleLogin = () => {
  userStore.setAuthModalVisible(true)
}

// 初始化数据
onMounted(() => {
  if (isLoggedIn.value) {
    myPageStore.fetchMembershipInfo()
  }
})
</script>

<style scoped lang="scss">
.pages-status {
  margin-bottom: 32px;
}

.status-card {
  padding: 24px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 12px;
  border: 1px solid #e9ecef;
}

.status-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;

  .header-icon {
    font-size: 24px;
    color: #4b5563;
  }

  .status-title {
    font-size: 1.125rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
  }
}

.status-content {
  .pages-count {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 12px;

    .count-number {
      font-size: 2.5rem;
      font-weight: 700;
      color: #3076ff;
      line-height: 1;
    }

    .count-unit {
      font-size: 1rem;
      color: #6b7280;
    }
  }

  .expiry-hint {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.875rem;
    color: #ef4444;
    background-color: #fef2f2;
    padding: 8px 12px;
    border-radius: 6px;
    width: fit-content;

    .hint-icon {
      font-size: 16px;
    }
  }
}

// 未登录提示样式
.guest-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: #6b7280;

  .login-link {
    color: #3076ff;
    font-weight: 600;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    text-decoration: underline;

    &:hover {
      color: #1e5fff;
    }
  }
}

// 骨架屏样式
.loading-state {
  .skeleton-line {
    background: #e5e7eb;
    border-radius: 4px;
    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
  
  .w-20 { width: 5rem; }
  .h-32 { height: 2rem; }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: .5; }
}
</style>
